import { type User } from 'next-auth';
import axios, { type AxiosInstance, isAxiosError } from 'axios';
import { jwtDecode } from 'jwt-decode';

import { capitalize } from '~/shared';
import type { AuthModelView, DecodedMartinsToken } from '~/types';
import { Project } from '~/types/next-auth';

import {
  MartinsAuthError,
  type MartinsAuthResponse,
  type MartinsMFAAuthResponse,
} from '../types';

export function getAuthErroFromMessage(message: string | null) {
  const errorCode = message?.split(': ')[1];
  if (!errorCode || !Object.keys(MartinsAuthError).includes(errorCode)) {
    return MartinsAuthError[18];
  }
  return MartinsAuthError[errorCode as keyof typeof MartinsAuthError];
}

export function makeMartinsUser({
  token,
  serviceToken,
  refreshToken,
  name,
  email,
  role,
  registration,
  projects,
  manufacturerId,
  firstAccess,
}: {
  token: string;
  serviceToken: string;
  refreshToken: string;
  name: string;
  email: string;
  role?: string;
  registration: string;
  projects: Project[] | null;
  manufacturerId?: number | null;
  firstAccess?: boolean;
}) {
  return {
    name: capitalize(name),
    email,
    token,
    role,
    serviceToken,
    refreshToken,
    registration,
    projects,
    manufacturerId,
    firstAccess,
  } as User;
}

export async function authWithMFA(
  axiosInstance: AxiosInstance,
  username: string,
  password: string,
  mfaCode: string
) {
  if (!process.env.MARTINS_AUTH_APP_CERTIFICATE) {
    throw new Error(MartinsAuthError[16]);
  }

  if (!process.env.MARTINS_AUTH_API_URL_MFA) {
    throw new Error(MartinsAuthError[16]);
  }

  try {
    if (!process.env.MARTINS_AUTH_API_URL_MFA) {
      throw new Error(`MFA_URL: "${process.env.MARTINS_AUTH_API_URL_MFA}"`);
    }
    // Step 1: Authenticate with MFA using the new endpoint
    const mfaAuthResponse = await axios.post<MartinsMFAAuthResponse>(
      process.env.MARTINS_AUTH_API_URL_MFA,
      {
        Usuario: username,
        Senha: password,
        TokenCode: mfaCode,
        AdAutenticacao: 'MRT',
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'X-ARR-ClientCert': process.env.MARTINS_AUTH_APP_CERTIFICATE,
        },
      }
    );

    if (
      !mfaAuthResponse.data.sucesso ||
      !mfaAuthResponse.data.retornoAutenticacao.token
    ) {
      throw new Error(MartinsAuthError[7]);
    }

    const martinsToken = mfaAuthResponse.data.retornoAutenticacao.token;
    const decodedToken = jwtDecode<DecodedMartinsToken>(martinsToken);

    const serviceAuthorization = await axiosInstance.post<AuthModelView>(
      '/Autenticacao/Auth',
      {
        accessToken: martinsToken,
      }
    );

    if (!serviceAuthorization.data.token) throw new Error(MartinsAuthError[7]);

    const user = makeMartinsUser({
      token: martinsToken,
      serviceToken: serviceAuthorization.data.token,
      refreshToken: serviceAuthorization.data.refreshToken,
      name: serviceAuthorization.data.user.name,
      email: serviceAuthorization.data.user.email,
      role: serviceAuthorization.data.user.role,
      registration: decodedToken.matricula,
      projects: serviceAuthorization.data.user.projects,
    });

    return user;
  } catch (error) {
    if (isAxiosError(error)) {
      if (error.code === 'ECONNREFUSED') {
        throw new Error(MartinsAuthError[17]);
      }
      if (!error.response?.data.mensagem) {
        throw new Error(error.code);
      }
      throw new Error(getAuthErroFromMessage(error.response?.data.mensagem));
    }
    throw new Error(MartinsAuthError[12]);
  }
}

export async function authWithUsernameAndPassword(
  axiosInstance: AxiosInstance,
  username: string,
  password: string,
  mfaCode?: string
) {
  // Staging usa apenas o endpoint sem MFA
  if (mfaCode && process.env.NEXT_PUBLIC_ENV !== 'staging') {
    return authWithMFA(axiosInstance, username, password, mfaCode);
  }
  if (
    !process.env.MARTINS_AUTH_API_URL ||
    !process.env.MARTINS_AUTH_APP_CERTIFICATE
  ) {
    throw new Error(MartinsAuthError[16]);
  }

  try {
    const authResponse = await axios.post<MartinsAuthResponse>(
      process.env.MARTINS_AUTH_API_URL,
      {
        Usuario: username,
        Senha: password,
        AdAutenticacao: 'MRT',
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'X-ARR-ClientCert': process.env.MARTINS_AUTH_APP_CERTIFICATE,
        },
      }
    );

    if (!authResponse.data.token) throw new Error(MartinsAuthError[7]);

    const decodedToken = jwtDecode<DecodedMartinsToken>(
      authResponse.data.token
    );

    const serviceAuthorization = await axiosInstance.post<AuthModelView>(
      '/Autenticacao/Auth',
      {
        accessToken: authResponse.data.token,
      }
    );

    if (!serviceAuthorization.data.token) throw new Error(MartinsAuthError[7]);

    const user = makeMartinsUser({
      token: authResponse.data.token,
      serviceToken: serviceAuthorization.data.token,
      refreshToken: serviceAuthorization.data.refreshToken,
      name: serviceAuthorization.data.user.name,
      email: serviceAuthorization.data.user.email,
      role: serviceAuthorization.data.user.role,
      registration: decodedToken.matricula,
      projects: serviceAuthorization.data.user.projects,
      manufacturerId: serviceAuthorization.data.user.manufacturerId,
    });

    return user;
  } catch (error) {
    if (isAxiosError(error)) {
      if (error.code === 'ECONNREFUSED') {
        throw new Error(MartinsAuthError[17]);
      }
      if (!error.response?.data.mensagem) {
        throw new Error(error.code);
      }
      throw new Error(getAuthErroFromMessage(error.response?.data.mensagem));
    }
    throw new Error(MartinsAuthError[12]);
  }
}

export async function authWithFabricaCredentials(
  axiosInstance: AxiosInstance,
  username: string,
  password: string
) {
  try {
    const authResponse = await axiosInstance.post<AuthModelView>(
      '/Autenticacao/loginFabrica',
      {
        username,
        password,
      }
    );

    if (!authResponse.data.token) throw new Error(MartinsAuthError[7]);

    const user = makeMartinsUser({
      token: '',
      serviceToken: authResponse.data.token,
      refreshToken: authResponse.data.refreshToken,
      name: authResponse.data.user.name,
      email: authResponse.data.user.email,
      role: authResponse.data.user.role,
      registration: '',
      projects: authResponse.data.user.projects,
      manufacturerId: authResponse.data.user.manufacturerId,
      firstAccess: authResponse.data.user.firstAccess,
    });

    return user;
  } catch (error) {
    if (isAxiosError(error)) {
      if (error.code === 'ECONNREFUSED') {
        throw new Error(MartinsAuthError[17]);
      }
      if (!error.response?.data.mensagem) {
        throw new Error(error.code);
      }
      throw new Error(getAuthErroFromMessage(error.response?.data.mensagem));
    }
    throw new Error(MartinsAuthError[12]);
  }
}

export async function authRefreshToken(
  axiosInstance: AxiosInstance,
  martinsToken: string,
  accessToken: string,
  refreshToken: string,
  registration: string
) {
  if (!refreshToken && !accessToken) throw new Error(MartinsAuthError[13]);

  try {
    const authResponse = await axiosInstance.post<AuthModelView>(
      `/Autenticacao/refresh-token`,
      {
        accessToken,
        refreshToken,
      },
      { headers: { 'Content-Type': 'application/json', Accept: '*/*' } }
    );

    if (!authResponse.data.token) throw new Error(MartinsAuthError[7]);

    const user = makeMartinsUser({
      token: martinsToken,
      serviceToken: authResponse.data.token,
      refreshToken: authResponse.data.refreshToken,
      name: authResponse.data.user.name,
      email: authResponse.data.user.email,
      role: authResponse.data.user.role,
      registration,
      projects: authResponse.data.user.projects,
    });

    return user;
  } catch (error) {
    if (isAxiosError(error)) {
      if (error.code === 'ECONNREFUSED') {
        throw new Error(MartinsAuthError[17]);
      }
      if (!error.response?.data.mensagem) {
        throw new Error(error.code);
      }
      throw new Error(getAuthErroFromMessage(error.response?.data.mensagem));
    }
    throw new Error(MartinsAuthError[12]);
  }
}
